import { useCallback, useMemo, useState } from "react";

import { useGetProductStats, useGetProducts } from "@/api";
import type { GetProductsParams } from "@/api/schemas/getProductsParams";
import { useSessionStore } from "@/stores/session.store";

const PER_PAGE = 10;

export function useProductsList() {
  const activeStoreId = useSessionStore((state) => state.activeStoreId);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const params = useMemo((): GetProductsParams | undefined => {
    if (activeStoreId === null) return undefined;

    return {
      store_id: activeStoreId,
      page,
      per_page: PER_PAGE,
      search: search.trim() || undefined,
    };
  }, [activeStoreId, page, search]);

  const { data, isLoading, isError } = useGetProducts(params, {
    query: { enabled: activeStoreId !== null },
  });

  const { data: statsData, isLoading: isStatsLoading } = useGetProductStats(
    activeStoreId !== null ? { store_id: activeStoreId } : undefined,
    { query: { enabled: activeStoreId !== null } },
  );

  const products = data?.data?.data ?? [];
  const meta = data?.data?.meta;
  const stats = statsData?.data?.data;

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  return {
    products,
    meta,
    stats,
    page,
    setPage,
    search,
    setSearch: handleSearchChange,
    isLoading,
    isStatsLoading,
    isError,
    isEmpty: !isLoading && !isStatsLoading && (stats?.total_count ?? 0) === 0,
  };
}
